import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { Btn, Card, Field, Input, Textarea } from "../components/Shared";
import { invalidatePortalConfig } from "../portalConfig";

// ── Management -> Sign-in page links (2026-11-04) ────────────────────────────
//
// The list the sign-in page shows under *Help and documentation*: manuals,
// tutorials, a wiki. It replaced PORTAL_GUIDE_URL, one link only a deployment
// could change. Backend: `routers/login_links.py` and `services/login_links.py`.
//
//   GET /api/admin/login-links  {links: [{title, url, description}]}
//   PUT /api/admin/login-links  {links} -> the GET body; 422 with one sentence
//                               for a list the server refuses
//
// **The links are public** — `/auth/config` sends them to anybody — and the
// card says so above the list. The server is the check (at most 20, a one-line
// title of 1-100 characters, an http(s) address with a host, a description of
// at most 300); the screen shows the server's sentence rather than guessing it.
//
// Rows are edited here and saved together. The keyboard focus stays in the card
// after Save (which is disabled again once saved), Remove and the move buttons —
// the button pressed may move or go away, and focus must not fall to <body>.
// `Btn` takes no ref or data attribute (componentProps.test.js), so each sits in
// a <span data-action> and the button is found inside it.

const MAX_LINKS = 20;
const SR_ONLY = { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden",
                  clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 };

let nextKey = 0;
const withKey = l => ({ key: `link-${nextKey++}`, title: l.title || "", url: l.url || "",
                        description: l.description || "" });
const plain = rows => rows.map(({ title, url, description }) =>
  ({ title: title.trim(), url: url.trim(), description: description.trim() }));
const same = (rows, stored) => JSON.stringify(plain(rows)) === JSON.stringify(plain((stored || []).map(withKey)));

// After React has committed a change, focus what `pick` returns.
const focusSoon = pick => setTimeout(() => { const el = pick(); if (el) el.focus(); }, 0);

export default function LoginLinksTab() {
  const [rows, setRows]     = useState(null);
  const [stored, setStored] = useState([]);
  const [error, setError]   = useState(null);
  const [saved, setSaved]   = useState(false);
  const [busy, setBusy]     = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    api.get("/api/admin/login-links")
      .then(r => { const links = r?.links || []; setStored(links); setRows(links.map(withKey)); })
      .catch(e => { setError(e.message); setRows([]); });
  }, []);

  const button = sel => () => boxRef.current?.querySelector(`${sel} button:not([disabled])`);
  const firstInputOf = key => () => boxRef.current?.querySelector(`[data-link-key="${key}"] input`);

  // A row's button of the same kind if it is still enabled, else any enabled one in that row,
  // else Add link.
  const focusInRow = (key, which) => focusSoon(() => {
    const box = boxRef.current;
    if (!box) return null;
    const row = key && box.querySelector(`[data-link-key="${key}"]`);
    if (row) {
      return row.querySelector(`[data-action="${which}"] button:not([disabled])`)
          || row.querySelector("[data-action] button:not([disabled])");
    }
    return button('[data-action="add"]')() || box.querySelector("input");
  });

  const edit = (key, field) => e => {
    const value = e.target.value;
    setSaved(false);
    setRows(rs => rs.map(r => (r.key === key ? { ...r, [field]: value } : r)));
  };

  const add = () => {
    const row = withKey({});
    setSaved(false);
    setRows(rs => [...rs, row]);
    focusSoon(firstInputOf(row.key));
  };

  const remove = key => {
    const i = rows.findIndex(r => r.key === key);
    const neighbour = rows[i + 1] || rows[i - 1];
    setSaved(false);
    setRows(rs => rs.filter(r => r.key !== key));
    focusInRow(neighbour?.key, "remove");
  };

  const move = (key, by) => {
    setSaved(false);
    setRows(rs => {
      const i = rs.findIndex(r => r.key === key);
      const j = i + by;
      if (i < 0 || j < 0 || j >= rs.length) return rs;
      const next = rs.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    focusInRow(key, by < 0 ? "up" : "down");
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const r = await api.put("/api/admin/login-links", { links: plain(rows) });
      const links = r?.links || [];
      setStored(links);
      setRows(links.map(withKey));
      setSaved(true);
      invalidatePortalConfig();
      // Save is disabled again now, so the focus goes to Add link.
      focusSoon(button('[data-action="add"]'));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (rows === null) {
    return (
      <Card>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Sign-in page links</h3>
        <p style={{ margin: "8px 0 0", fontSize: 13, color: "#6b7280" }}>Loading…</p>
      </Card>
    );
  }

  const unchanged = same(rows, stored);

  return (
    <Card>
      <div ref={boxRef}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Sign-in page links</h3>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: "#374151" }}>
          The sign-in page lists these under <em>Help and documentation</em>, below the sign-in box:
          manuals, tutorials, a wiki. Each opens in a new tab. With no links, the sign-in page shows
          nothing extra.
        </p>
        <p role="note" style={{ margin: "10px 0 16px", padding: "10px 14px", borderRadius: 8, fontSize: 13,
                                background: "#fffbeb", border: "1px solid #fcd34d", color: "#78350f" }}>
          <strong>These links are public.</strong> Anybody who opens the sign-in page sees them,
          signed in or not — a link to an internal page still shows its address.
        </p>

        {error && <div role="alert" style={{ marginBottom: 12, color: "#b91c1c", fontSize: 13 }}>{error}</div>}
        {saved && <div role="status" style={{ marginBottom: 12, color: "#065f46", fontSize: 13 }}>
          Saved. The sign-in page shows {stored.length === 0 ? "no links" : stored.length === 1 ? "this link" : `these ${stored.length} links`}.
        </div>}

        {rows.length === 0 && (
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>No links yet.</p>
        )}

        <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
          {rows.map((r, i) => (
            <li key={r.key} data-link-key={r.key}>
              <fieldset style={{ margin: 0, border: "1px solid #e5e7eb", borderRadius: 12, padding: "12px 14px", minWidth: 0 }}>
                <legend style={{ padding: "0 6px", fontSize: 13, fontWeight: 600, color: "#374151" }}>
                  Link {i + 1}{r.title.trim() ? `: ${r.title.trim()}` : ""}
                </legend>
                <div data-form-grid style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                  <Field label="Title" required>
                    <Input value={r.title} onChange={edit(r.key, "title")} maxLength={100}
                           placeholder="e.g. User manual" />
                  </Field>
                  <Field label="Address" required hint="https:// or http:// only.">
                    <Input type="url" inputMode="url" value={r.url} onChange={edit(r.key, "url")}
                           placeholder="https://…" />
                  </Field>
                </div>
                <div style={{ marginTop: 12 }}>
                  <Field label="Description (optional)" hint="Plain text, up to 300 characters.">
                    <Textarea rows={2} value={r.description} onChange={edit(r.key, "description")} maxLength={300} />
                  </Field>
                </div>
                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span data-action="up">
                    <Btn variant="secondary" disabled={busy || i === 0} onClick={() => move(r.key, -1)}>
                      Move up<span style={SR_ONLY}> link {i + 1}</span>
                    </Btn>
                  </span>
                  <span data-action="down">
                    <Btn variant="secondary" disabled={busy || i === rows.length - 1} onClick={() => move(r.key, 1)}>
                      Move down<span style={SR_ONLY}> link {i + 1}</span>
                    </Btn>
                  </span>
                  <span data-action="remove">
                    <Btn variant="secondary" disabled={busy} onClick={() => remove(r.key)}>
                      Remove<span style={SR_ONLY}> link {i + 1}</span>
                    </Btn>
                  </span>
                </div>
              </fieldset>
            </li>
          ))}
        </ol>

        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span data-action="add">
            <Btn variant="secondary" disabled={busy || rows.length >= MAX_LINKS} onClick={add}>Add link</Btn>
          </span>
          <Btn onClick={save} disabled={busy || unchanged}>{busy ? "Saving…" : "Save links"}</Btn>
          {rows.length >= MAX_LINKS && (
            <span style={{ fontSize: 12, color: "#6b7280" }}>At most {MAX_LINKS} links.</span>
          )}
        </div>
      </div>
    </Card>
  );
}
